import React, { useState, useEffect, useRef } from "react";
import { Children, cloneElement } from "react";
import { Calendar, momentLocalizer, Views } from "react-big-calendar";
import moment from "moment"; // Required for react-big-calendar localizer only
import dayjs from "dayjs";
import "react-big-calendar/lib/css/react-big-calendar.css";
import { listUsers } from "../actions/userActions";
import { DatePicker, Space } from "antd";

import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Loader from "../core/Loader";
import Layout from "../core/Layout";
import AddAppVaccineModal from "./modal/addAppointment";
import { useAppointments, useInvalidateAppointments } from "./api/app";

const localizer = momentLocalizer(moment);

const TimelineCalendar = () => {
    const [appointmentData, setAppointmentData] = useState([]);
    const [doctorColors, setDoctorColors] = useState({});
    const [selectedDate, setSelectedDate] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [gtdate, setGtDate] = useState(dayjs().startOf("month").toDate());
    const [ltdate, setLtDate] = useState(dayjs().endOf("month").toDate());
    const [dateChange, setDateChange] = useState(false);
    const [selectedTime, setSelectedTime] = useState(null);
    const [currentView, setCurrentView] = useState("day");
    const invalidate = useInvalidateAppointments();

    const { data: appointments, isLoading } = useAppointments({
        gtdate,
        ltdate,
    });

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const userList = useSelector((state) => state.userList);
    const { users } = userList;

    const userLogin = useSelector((state) => state.userLogin);
    const { userInfo } = userLogin;

    useEffect(() => {
        if (userInfo) {
            dispatch(listUsers());
        } else {
            navigate("/signin");
        }
    }, [dispatch, userInfo, navigate]);

    const handleViewChange = (view) => {
        setTimeout(() => {
            setCurrentView(view);
        }, 2000);
    };

    const getDurationInMinutes = (duration) => {
        const parts = (duration || "").match(/\d+/g);
        if (!parts) return 0;
        if (duration.includes("hr") && duration.includes("min")) {
            return parseInt(parts[0]) * 60 + parseInt(parts[1]);
        } else if (duration.includes("hr")) {
            return parseInt(parts[0]) * 60;
        } else {
            return parseInt(parts[0]);
        }
    };

    const colors = [
        "#B0C4DE",
        "#F0FFFF",
        "#B0E0E6",
        "#98FB98",
        "#ADD8E6",
        "#FFB6C1",
        "#FFA07A",
        "#20B2AA",
    ];

    const getColorByIndex = (index) => colors[index % colors.length];

    useEffect(() => {
        if (users && users.length > 0) {
            let updatedColors = { ...doctorColors };
            let count = 0;

            users.forEach((user) => {
                if (user.role === 1 && !updatedColors[user._id]) {
                    updatedColors[user._id] = getColorByIndex(count);
                    count++;
                }
            });

            setDoctorColors(updatedColors);
        }
    }, [users]);

    useEffect(() => {
        if (appointments && appointments.length > 0) {
            const filteredAppointments = appointments.filter((appointment) => {
                const fullDate = dayjs(appointment.date).toDate();
                return (
                    (!gtdate || fullDate >= gtdate) &&
                    (!ltdate || fullDate <= ltdate)
                );
            });

            const formattedAppointments = filteredAppointments.map(
                (appointment) => {
                    const startDate = dayjs(appointment.date).toDate();
                    const startTime = dayjs(appointment.time, "HH:mm").toDate();

                    const start = new Date(
                        startDate.getFullYear(),
                        startDate.getMonth(),
                        startDate.getDate(),
                        startTime.getHours(),
                        startTime.getMinutes()
                    );

                    const durationMinutes = getDurationInMinutes(
                        appointment.duration
                    );
                    const end = new Date(
                        start.getTime() + durationMinutes * 60000
                    );

                    return {
                        title: `${appointment.patient.firstName} - ${appointment.patient.patientNumber}, Dr.${appointment.doctor.name}`,
                        start,
                        end,
                        desc: appointment,
                        doctorId: appointment.doctor?._id || "unknown",
                    };
                }
            );

            setAppointmentData(formattedAppointments);
        }
    }, [appointments]);

    const formats = {
        timeGutterFormat: (date, culture, localizer) =>
            localizer.format(date, "H:mm", culture),
    };

    const onSelectEvent = (event) => {
        if (event.desc.patient) {
            localStorage.setItem(
                "selectedPatient",
                JSON.stringify(event.desc.patient)
            );
        }
        window.open("/patient-app-details", "_blank");
    };

    const onSelectSlot = (slotInfo) => {
        const startTime = new Date(slotInfo.start);
        // Align to the nearest 15-minute interval
        const selected = dayjs(slotInfo.start).toDate();

        // Change to day view
        setCurrentView("day");
        const startMinutes = Math.floor(startTime.getMinutes() / 15) * 15;
        startTime.setMinutes(startMinutes);
        startTime.setSeconds(0);
        startTime.setMilliseconds(0);
        const formattedTime = dayjs(startTime).format("HH:mm");
        const formatted = dayjs(new Date(slotInfo.slots[0])).format(
            "YYYY-MM-DD"
        );
        setSelectedTime(formattedTime);
        setSelectedDate(formatted);
        setShowModal(true);
    };

    const handleDateChange = (date) => {
        if (date) {
            setGtDate(dayjs(date.format("YYYY-MM-DD")).toDate());

            setLtDate(
                dayjs(date.format("YYYY-MMM-DD")).endOf("month").toDate()
            );
            setDateChange(true);
            setCurrentView("Month");
        }
    };

    const handleRangeChange = (range) => {
        if (Array.isArray(range)) {
            const sorted = range.sort((a, b) => a - b);
            setGtDate(sorted[0]);
            setLtDate(sorted[sorted.length - 1]);
        } else if (range instanceof Date) {
            const dayStart = dayjs(range).startOf("day").toDate();
            const dayEnd = dayjs(range).endOf("day").toDate();
            setGtDate(dayStart);
            setLtDate(dayEnd);
        }
    };

    const eventPropGetter = (event) => {
        return {
            style: {
                backgroundColor: doctorColors[event.doctorId] || "lightgrey",
                color: "black",
                borderRadius: "5px",
                border: "none",
                padding: "5px",
            },
        };
    };

    const TouchCellWrapper = ({ children, value, onSelectSlot }) =>
        cloneElement(Children.only(children), {
            onClick: () => onSelectSlot({ action: "click", slots: [value] }),
        });

    const handleChange = (date) => {
        setGtDate(dayjs(date).startOf("month").toDate());
        setLtDate(dayjs(date).endOf("month").toDate());
    };

    const CustomToolbar = ({ label, onNavigate, view, views, onView }) => {
        return (
            <div className="rbc-toolbar">
                {view !== "day" && (
                    <span className="rbc-btn-group">
                        <button onClick={() => onNavigate("PREV")}>
                            {"<"}
                        </button>
                        <button onClick={() => onNavigate("TODAY")}>
                            Today
                        </button>
                        <button onClick={() => onNavigate("NEXT")}>
                            {">"}
                        </button>
                    </span>
                )}
                <span className="rbc-toolbar-label">{label}</span>

                <span className="rbc-btn-group">
                    {views.map((v, i) => (
                        <button
                            key={i}
                            className={v === view ? "rbc-active" : ""}
                            onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                onView(v);
                            }}
                        >
                            {v.charAt(0).toUpperCase() +
                                v.slice(1).replace("_", " ")}
                        </button>
                    ))}
                </span>
            </div>
        );
    };

    return (
        <>
            <style>
                {`
    .rbc-overlay {
      max-height: 300px;
      overflow-y: auto;
      z-index: 1000;
    }
  `}
            </style>
            {showModal && (
                <AddAppVaccineModal
                    show={showModal}
                    selectedTime={selectedTime}
                    onClose={() => {
                        invalidate();
                        setShowModal(false);
                    }}
                    selectedDate={selectedDate}
                />
            )}

            <Layout title={"Calendar"}>
                <div style={{ height: 700 }}>
                    <Space direction="horizontal" style={{ marginBottom: 16 }}>
                        <DatePicker onChange={handleDateChange} allowClear />
                    </Space>
                    {isLoading ? (
                        <Loader />
                    ) : (
                        <Calendar
                            events={appointmentData}
                            localizer={localizer}
                            date={currentView === "day" ? new Date() : gtdate}
                            defaultDate={new Date()}
                            views={["day", "month", "agenda"]}
                            defaultView={"day"}
                            step={2.5}
                            showMultiDayTimes
                            popup={true}
                            formats={formats}
                            selectable={true}
                            longPressThreshold={1}
                            onSelectSlot={onSelectSlot}
                            onSelectEvent={onSelectEvent}
                            onRangeChange={handleRangeChange}
                            onNavigate={handleChange}
                            onView={handleViewChange}
                            min={new Date().setHours(8, 0, 0, 0)}
                            eventPropGetter={eventPropGetter}
                            culture="en-US"
                            dayLayoutAlgorithm="no-overlap"
                            components={{
                                toolbar: (props) => (
                                    <CustomToolbar
                                        {...props}
                                        view={currentView}
                                    />
                                ),
                                dateCellWrapper: (props) => (
                                    <TouchCellWrapper
                                        {...props}
                                        onSelectSlot={onSelectSlot}
                                    />
                                ),
                            }}
                        />
                    )}
                </div>
            </Layout>
        </>
    );
};

export default TimelineCalendar;
